import { Typography } from '@mui/joy';
import { BarChart } from '@mui/x-charts/BarChart';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import useSWR from 'swr';
import { z } from 'zod';

import ReportGraphLegend from './reportGraphLegend';

import { useLocalizedReportTypeData } from '../../../hooks/fetcher/useReportTypeData';
import { getUrlString } from '../../../Page/customer/customerDashboard';
import LoadingComponent from '../../common/loadingComponent';
import NoDataFound from '../../common/noDataFound';

import { Card } from '~/components/ui/card';
import { DateRangePickerField } from '~/components/ui/date-picker';
import { Option, SelectField } from '~/components/ui/select';
import { Switch } from '~/components/ui/switch';
import { Text } from '~/components/ui/text';
import { usePartnerContext } from '~/contexts/partner/partnerContext';
import { useIntakeChannel } from '~/slices/intakeChannelSlice';
import { useForm } from '~/utils/forms';

interface ReportsDataObject {
  report_name: string;
  report_count: number;
}

interface DataFromApiObject {
  label: string | null;
  data: ReportsDataObject[];
}

interface BarChartObject {
  data: number[];
  fullLabel: string;
  label: string;
  stack: string;
}

const palette = [
  '#000107',
  '#070F38',
  '#0B1751',
  '#0E1E69',
  '#162C9A',
  '#1D3BCB',
  '#2449FC',
  '#506DFD',
  '#7C92FD',
  '#A7B6FE',
  '#003538',
  '#065B5B',
  '#008D8A',
  '#00B2AD',
  '#00DCD0',
  '#08F9EB',
  '#3FFFF5',
  '#84FFF9',
  '#C2FFFB',
];

const transformDataToChartData = (
  data: DataFromApiObject[],
  getLocalizedReportType: (reportType: string) => string,
) => {
  let labels: string[] = [];
  const legends: string[] = [];
  //get all labels first
  data.forEach((item) => {
    item?.data?.forEach((report) => {
      const reportName = report.report_name;
      if (legends.indexOf(reportName) === -1) legends.push(reportName ?? '-');
    });
  });
  //get all legends from api
  labels = data.map((item) => item.label || 'No Name');

  const transformedData: BarChartObject[] = [];
  //distribute the data based on the labels
  if (Array.isArray(legends)) {
    legends.map((legendItem) => {
      transformedData.push({
        fullLabel: legendItem,
        label:
          legendItem.length > 12
            ? `${getLocalizedReportType(legendItem).substring(0, 12)}...`
            : getLocalizedReportType(legendItem),
        data: Array.isArray(data)
          ? data.map((item) => {
              const report = item?.data?.find(
                (reportItem) => reportItem.report_name === legendItem,
              );
              return report?.report_count || 0;
            })
          : [],
        stack: 'total',
      });
    });
  }
  return {
    data: transformedData,
    labels: labels,
  };
};

const getCustomBarWidth = (labels: string[]) => {
  if (labels.length >= 1 && labels.length < 3) return 0.95;
  if (labels.length >= 3 && labels.length < 5) return 0.85;
  if (labels.length >= 5 && labels.length < 7) return 0.8;
  return 0.4;
};

const schema = z.object({
  date_range: z.object({
    from: z.coerce.date().optional(),
    to: z.coerce.date().optional(),
  }),
  filter_by: z.string(),
});
export type DashboardFilterFields = z.infer<typeof schema>;

const TotalReport = () => {
  const { selectedTenant } = usePartnerContext();
  const form = useForm({
    schema: schema,
    defaultValues: {
      filter_by: 'day',
    },
  });

  const from = form.watch('date_range')?.from;
  const to = form.watch('date_range')?.to;
  const filterBy = form.watch('filter_by');

  const link = 'dashboard/reports-count-type';
  const selectedChannel = useIntakeChannel().intakeChannelData.selectedChannel;
  const [totalReportDataList, setTotalReportDataList] = useState<
    BarChartObject[]
  >([]);
  const [ReportLabels, setReportLabels] = useState<string[]>([]);
  const { getLocalizedReportType } = useLocalizedReportTypeData();
  const intl = useIntl();
  const [hideDaysWithoutReports, setHideDaysWithoutReports] =
    useState<boolean>(true);
  const isDateRange = z
    .object({ dateStart: z.coerce.date(), dateEnd: z.coerce.date() })
    .safeParse({ dateStart: from, dateEnd: to }).success;
  const isFilteredByMonth = filterBy === 'month';

  const { data, error, isLoading } = useSWR(
    getUrlString(
      link,
      filterBy,
      from ? from.toISOString() : '',
      to ? to.toISOString() : '',
      selectedTenant.id,
      selectedChannel,
      hideDaysWithoutReports,
    ),
    {
      revalidateOnFocus: true,
    },
  );

  useEffect(() => {
    if (!isLoading && !error && data) {
      const reportData = transformDataToChartData(data, getLocalizedReportType);
      setTotalReportDataList(reportData.data);
      setReportLabels(reportData.labels);
    }
  }, [data, error, isLoading, filterBy, from, to, getLocalizedReportType]);

  useEffect(() => {
    if (isFilteredByMonth) {
      setHideDaysWithoutReports(true);
    }
  }, [isFilteredByMonth]);

  return (
    <Card className='border shadow-md'>
      <div className='flex justify-end gap-2'>
        <SelectField control={form.control} field='filter_by'>
          <Option value='day'>
            <FormattedMessage defaultMessage='Day' />
          </Option>
          <Option value='month'>
            <FormattedMessage defaultMessage='Month' />
          </Option>
        </SelectField>
        <DateRangePickerField control={form.control} field='date_range' />
      </div>
      <div className='flex flex-row justify-between'>
        <Typography level='body-md'>
          <FormattedMessage
            defaultMessage='Total Reports <span>(Report Type)</span>'
            values={{
              span: (chunks) => (
                <span
                  style={{
                    fontSize: '14px',
                    color: '#878787',
                  }}
                >
                  {chunks}
                </span>
              ),
            }}
          />
        </Typography>
        <div className='flex flex-row justify-center gap-2 items-center'>
          <Switch
            disabled={!isDateRange || isFilteredByMonth}
            checked={hideDaysWithoutReports}
            onChange={(e) => {
              setHideDaysWithoutReports(e.target.checked);
            }}
          />
          <Text level='body-sm'>
            <FormattedMessage defaultMessage='Hide days without reports' />
          </Text>
        </div>
      </div>
      {isLoading && <LoadingComponent isMinHeight />}
      {error && (
        <Typography level='body-md' color='danger'>
          {error.message}
        </Typography>
      )}
      {!isLoading && !error && totalReportDataList.length !== 0 && (
        <div className='flex flex-col h-full'>
          <ReportGraphLegend
            legends={totalReportDataList.map((dataLabel) =>
              getLocalizedReportType(dataLabel.label),
            )}
            tooltipText={totalReportDataList.map((dataFullLabel) =>
              getLocalizedReportType(dataFullLabel.fullLabel),
            )}
            colors={palette}
            align='left'
          />
          <BarChart
            colors={palette}
            axisHighlight={{ x: 'band', y: 'line' }}
            slotProps={{
              legend: {
                hidden: true,
                labelStyle: {
                  fontSize: 12,
                },
              },
            }}
            height={400}
            series={totalReportDataList}
            xAxis={[
              {
                scaleType: 'band',
                data: ReportLabels,
                //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                categoryGapRatio: getCustomBarWidth(ReportLabels),
                barGapRatio: 5,
              },
            ]}
            yAxis={[
              {
                tickMaxStep: totalReportDataList.reduce((max, item) => {
                  // This is to get the max value in the data array
                  const maxInItem = Math.max(...item.data);
                  return Math.max(max, maxInItem);
                }, -Infinity),
                tickMinStep: 1,
              },
            ]}
          />
        </div>
      )}
      {!isLoading && !error && totalReportDataList.length === 0 && (
        <NoDataFound
          message={intl.formatMessage({ defaultMessage: 'No data found' })}
        />
      )}
    </Card>
  );
};

export default TotalReport;
